import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ProfileCompletionService } from './profile.completion.service';
import { CommonSearchParam } from '../../../../core/interfaces/common-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';
import { Profile, Experience, Education } from '../../interfaces/profile/profile.interface';
import { convertToHttpParams } from '../../../../core/interfaces/common-search-param.interface';
import { environment } from '../../../../../environments/environment.dev';
import { ApiResponse } from '../../../../core/interfaces/ApiResponse';

@Injectable({
    providedIn: 'root'
})
export class ProfileCompletionApiService extends ProfileCompletionService {
    private http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiBaseUrl}v1/user/`;
    private readonly workExpUrl = `${environment.apiBaseUrl}v1/work-experience/`;
    private readonly educationUrl = `${environment.apiBaseUrl}v1/education/`;

    constructor() {
        super();
    }

    override findAll(searchParam: CommonSearchParam): Observable<PageResponse<Profile>> {
        return this.wrapRequest(
            this.http.get<PageResponse<Profile>>(this.baseUrl, { headers: this.headers, params: convertToHttpParams(searchParam) })
        );
    }

    override find(id: string): Observable<Profile> {
        return this.wrapRequest(
            this.http.get<ApiResponse<Profile>>(`${this.baseUrl}/${id}`, { headers: this.headers })
                .pipe(map(res => res.response))
        );
    }

    override create(data: Partial<Profile>): Observable<Profile> {
        return this.wrapRequest(
            this.http.post<ApiResponse<Profile>>(this.baseUrl, data, { headers: this.headers })
                .pipe(map(res => res.response))
        );
    }

    override update(id: string, data: Partial<Profile>): Observable<Profile> {
        return this.wrapRequest(
            this.http.put<ApiResponse<Profile>>(`${this.baseUrl}/${id}`, data, { headers: this.headers })
                .pipe(map(res => res.response))
        );
    }

    override delete(id: string): Observable<void> {
        return this.wrapRequest(
            this.http.delete<void>(`${this.baseUrl}/${id}`, { headers: this.headers })
        );
    }

    override getProfileDetails(): Observable<Profile> {
        return this.wrapRequest(
            this.http.get<ApiResponse<Profile>>(`${this.baseUrl}details`, { headers: this.headers })
                .pipe(map(res => res.response))
        );
    }

    override updateBasicInfo(data: Profile): Observable<Profile> {
        return this.wrapRequest(
            this.http.put<ApiResponse<Profile>>(`${this.baseUrl}basic-info`, data, { headers: this.headers })
                .pipe(map(res => res.response)),
            false
        );
    }

    override updateUserType(userType: string): Observable<void> {
        return this.wrapRequest(
            this.http.put<void>(`${this.baseUrl}update`, {}, {
                headers: this.headers,
                params: { userTypeEnum: userType }
            })
        );
    }

    override updateSkills(data: { skills: string[], careerObjective: string }): Observable<any> {
        return this.wrapRequest(
            this.http.post<ApiResponse<any>>(`${this.baseUrl}skills`, data, { headers: this.headers })
                .pipe(map(res => res.response)),
            false
        );
    }

    override uploadProfilePicture(uniqueId: string, file: File, isPrimary: boolean): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPrimary', isPrimary.toString());

        // We delete Content-Type to let the browser set it with the boundary for FormData
        return this.wrapRequest(
            this.http.post<any>(`${this.baseUrl}upload-profile/${uniqueId}`, formData, {
                headers: this.headers.delete('Content-Type')
            })
        );
    }

    override createOrUpdateEducation(data: Education[]): Observable<Education[]> {
        return this.wrapRequest(
            this.http.post<ApiResponse<Education[]>>(`${this.educationUrl}create-or-update`, data, { headers: this.headers })
                .pipe(map(res => res.response)),
            false
        );
    }

    override getCurrentUserEducation(): Observable<Education[]> {
        return this.wrapRequest(
            this.http.get<ApiResponse<Education[]>>(`${this.educationUrl}current-user`, { headers: this.headers })
                .pipe(map(res => res.response))
        );
    }

    override deleteEducation(uniqueId: string): Observable<void> {
        return this.wrapRequest(
            this.http.delete<void>(`${this.educationUrl}${uniqueId}`, { headers: this.headers }),
            false
        );
    }

    override getCurrentWorkExperience(): Observable<Experience[]> {
        return this.wrapRequest(
            this.http.get<ApiResponse<Experience[]>>(`${this.workExpUrl}current-user`, { headers: this.headers })
                .pipe(map(res => res.response))
        );
    }

    override createWorkExperience(data: Experience[]): Observable<Experience[]> {
        return this.wrapRequest(
            this.http.post<ApiResponse<Experience[]>>(`${this.workExpUrl}create-or-update`, data, { headers: this.headers })
                .pipe(map(res => res.response)),
            false
        );
    }

    override updateWorkExperience(uniqueId: string, data: Experience): Observable<Experience> {
        return this.wrapRequest(
            this.http.put<ApiResponse<Experience>>(`${this.workExpUrl}update`, data, {
                headers: this.headers,
                params: { experincedUniqueld: uniqueId }
            }).pipe(map(res => res.response)),
            false
        );
    }

    override deleteExperience(uniqueId: string): Observable<void> {
        return this.wrapRequest(
            this.http.delete<void>(`${this.workExpUrl}${uniqueId}`, { headers: this.headers }),
            false
        );
    }

    override getExperiencedProjects(): Observable<any> {
        return this.wrapRequest(
            this.http.get<any>(`${this.workExpUrl}user-experinced-projects`, { headers: this.headers })
        );
    }

    override getExperienceRange(): Observable<any> {
        return this.wrapRequest(
            this.http.get<any>(`${this.workExpUrl}experience-range`, { headers: this.headers })
        );
    }
}
